/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit, signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { ConfirmActionDialogComponent } from '../common/community/feed/actions/confirm-action-dialog.component';

// /forums/:discussionId/page/:page in the 2026 design: breadcrumb, the original post as a card with the shared
// action bar, a moderator menu (edit, lock, delete) and an "About this topic" sidebar. Replies are the
// 2026 ut-comment-thread, which keeps the :page paging, #comment_ID deep links and the duplicate-first-comment rule.
// Delete asks in a dialog (the legacy page swapped buttons inline), and editing goes to editDiscussion.
@Component({
  selector: 'ut-topic-page',
  templateUrl: './topic-page.component.html',
  styleUrls: ['./topic-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TopicPageComponent implements OnInit {
  // The `singlediscussionpage` state's topic.
  @Input({ required: true }) discussion: any;
  // The root state's `user`: null when logged out.
  @Input() user: any;

  body: SafeHtml = '';
  shareUrl = '';
  // The thread's count once it loads, so the action bar and the thread heading agree.
  threadCount = signal<number | null>(null);

  constructor(
    @Inject('DiscussionService') readonly DiscussionService: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('$filter') private $filter: any,
    @Inject('$state') private $state: any,
    private dialogService: DialogService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.body = this.sanitizer.bypassSecurityTrustHtml(this.$filter('uMarkdown')(this.discussion.content));
    this.shareUrl = this.$state.href('singlediscussion', { discussionId: this.discussion.id }, { absolute: true });
  }

  get title(): string {
    return this.$filter('cleanUp')(this.discussion.title);
  }

  get replies(): number {
    return this.threadCount() ?? (this.discussion.post_count || 0);
  }

  // "Proven Tester · Brazil", skipping the parts a user doesn't have; the template adds the relative date.
  get authorMeta(): string {
    return [this.discussion.user?.user_title, this.discussion.user?.country?.name].filter(Boolean).join(' · ');
  }

  // The hide action mutates `discussion.state`; a listener on its output re-renders this OnPush view.
  refresh(): void {
    this.cdr.markForCheck();
  }

  get canManage(): boolean {
    const p = this.discussion.current_user_permissions || {};
    return !!(p.destroy || p.lock);
  }

  toggleLock(): void {
    this.DiscussionService.toggleLock(this.discussion).then((res) => {
      this.discussion.locked = res.data.locked;
      this.AlertService.add(res.data.locked ? 'Topic locked' : 'Topic unlocked');
      this.cdr.markForCheck();
    }, () => this.AlertService.addDanger('Request failed'));
  }

  confirmDelete(): void {
    this.dialogService.open(ConfirmActionDialogComponent, {
      data: {
        title: 'Delete this topic?',
        text: 'The topic and its replies will be removed from the forums. This can\'t be undone.',
        confirmLabel: 'Delete',
        destructive: true,
      },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.DiscussionService.remove(this.discussion.id).then(() => {
        this.$state.go('discussion');
        this.AlertService.add('Topic successfully deleted');
      }, () => this.AlertService.addDanger('Couldn\'t delete the Forum Topic'));
    });
  }
}
